import { useTranslation } from 'react-i18next';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { PresenceUser } from '@/lib/graphql/generated/graphql';
import { usePresenceSubscription } from '@/lib/hooks/usePresence';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface PresenceIndicatorProps {
  dealId: string;
}

// Predefined Tailwind color classes for user avatars
const USER_COLORS = [
  'bg-blue-600',
  'bg-green-600',
  'bg-purple-600',
  'bg-pink-600',
  'bg-orange-600',
  'bg-cyan-600',
  'bg-indigo-600',
  'bg-rose-600',
  'bg-teal-600',
  'bg-amber-600',
  'bg-violet-600',
  'bg-emerald-600',
] as const;

// Generate a consistent color class for a given username
function getUserColor(username: string): string {
  let hash = 0;
  for (let i = 0; i < username.length; i++) {
    hash = username.charCodeAt(i) + ((hash << 5) - hash);
  }

  const index = Math.abs(hash) % USER_COLORS.length;
  return USER_COLORS[index];
}

const formatDate = (dateString: Date) => {
  try {
    return format(dateString, 'd/MM (HH:mm)', { locale: fr });
  } catch {
    return dateString;
  }
};

function sortUsers(a: PresenceUser, b: PresenceUser) {
  const aDate = Math.max(
    a.lastViewedAt?.getTime() ?? 0,
    a.lastUpdatedAt?.getTime() ?? 0
  );
  const bDate = Math.max(
    b.lastViewedAt?.getTime() ?? 0,
    b.lastUpdatedAt?.getTime() ?? 0
  );
  return bDate - aDate;
}

function getMessage(user: PresenceUser, t: (key: string) => string) {
  if (user.isOnline) {
    return t('components.presence_indicator.online');
  } else if (!(user.lastViewedAt || user.lastUpdatedAt)) {
    return '';
  } else if (
    user.lastViewedAt &&
    user.lastViewedAt > (user.lastUpdatedAt ?? 0)
  ) {
    return (
      t('components.presence_indicator.last_viewed') +
      ' : ' +
      formatDate(user.lastViewedAt)
    );
  } else if (
    user.lastUpdatedAt &&
    user.lastUpdatedAt > (user.lastViewedAt ?? 0)
  ) {
    return (
      t('components.presence_indicator.last_updated') +
      ' : ' +
      formatDate(user.lastUpdatedAt)
    );
  }
}

export function PresenceIndicator({ dealId }: PresenceIndicatorProps) {
  const { otherUsers } = usePresenceSubscription(dealId);
  const { t } = useTranslation();

  if (otherUsers.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-2 text-sm">
      <TooltipProvider>
        <div className="flex items-center gap-1">
          {otherUsers.sort(sortUsers).map((user) => (
            <Tooltip key={user.userId}>
              <TooltipTrigger>
                <div
                  className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium text-white ${getUserColor(user.username)} ${!user.isOnline ? 'opacity-40' : ''}`}
                  title={user.username}
                >
                  {user.username.slice(0, 2).toUpperCase()}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {user.username}
                <br />
                {getMessage(user, t)}
              </TooltipContent>
            </Tooltip>
          ))}
        </div>
      </TooltipProvider>
    </div>
  );
}
